export const KELIRIJI_PACKAGE = 'com.keliriji.app';
export const KELIRIJI_IOS_APPLE_ID = '6776661422';
export const KELIRIJI_IOS_STORE_URL =
  'https://apps.apple.com/cn/app/id6776661422';
export const KELIRIJI_DOWNLOAD_URL = 'https://www.keliriji.com/download';
export const OFFICIAL_DOWNLOAD_URL = 'https://kelilearning.com/download';

export type StoreKey =
  | 'ios'
  | 'huawei'
  | 'honor'
  | 'xiaomi'
  | 'oppo'
  | 'oneplus'
  | 'vivo'
  | 'tencent';

const ANDROID_HTTPS = {
  tencent: `https://a.app.qq.com/o/simple.jsp?pkgname=${KELIRIJI_PACKAGE}`,
  xiaomi: `https://app.mi.com/details?id=${KELIRIJI_PACKAGE}`,
  huawei: 'https://appgallery.huawei.com/app/C118444885',
  honor: 'https://appgallery.honor.com.cn/app/C104582508',
  oppo: 'https://i.open.oppomobile.com/detail/index?id=37467697',
  vivo: 'https://h5.appstore.vivo.com.cn/#/details?appId=106118724',
} as const;

export type StoreTileId = StoreKey;

export type StoreTile = {
  id: StoreTileId;
  label: string;
  logo: string;
  href: string;
};

export const STORE_TILES: StoreTile[] = [
  { id: 'ios', label: 'Apple', logo: '/store-logos/apple.svg', href: KELIRIJI_IOS_STORE_URL },
  { id: 'huawei', label: '华为', logo: '/store-logos/huawei.svg', href: ANDROID_HTTPS.huawei },
  { id: 'honor', label: '荣耀', logo: '/store-logos/honor.svg', href: ANDROID_HTTPS.honor },
  { id: 'xiaomi', label: '小米', logo: '/store-logos/xiaomi.svg', href: ANDROID_HTTPS.xiaomi },
  { id: 'oppo', label: 'OPPO', logo: '/store-logos/oppo.svg', href: ANDROID_HTTPS.oppo },
  { id: 'oneplus', label: '1+', logo: '/store-logos/oneplus.svg', href: ANDROID_HTTPS.oppo },
  { id: 'vivo', label: 'vivo', logo: '/store-logos/vivo.svg', href: ANDROID_HTTPS.vivo },
  { id: 'tencent', label: '应用宝', logo: '/store-logos/tencent.svg', href: ANDROID_HTTPS.tencent },
];

const STORE_ALIASES: Record<string, StoreKey> = {
  ios: 'ios',
  iphone: 'ios',
  ipad: 'ios',
  appstore: 'ios',
  huawei: 'huawei',
  honor: 'honor',
  xiaomi: 'xiaomi',
  mi: 'xiaomi',
  redmi: 'xiaomi',
  poco: 'xiaomi',
  oppo: 'oppo',
  oneplus: 'oneplus',
  '1+': 'oneplus',
  '1plus': 'oneplus',
  oxygen: 'oneplus',
  oxygenos: 'oneplus',
  realme: 'oppo',
  vivo: 'vivo',
  tencent: 'tencent',
  yyb: 'tencent',
  myapp: 'tencent',
};

export function parseStoreKey(raw: string | null | undefined): StoreKey | null {
  const key = String(raw || '').trim().toLowerCase();
  if (!key || key === 'google' || key === 'play') return null;
  return STORE_ALIASES[key] ?? null;
}

const STORE_HREFS: Record<StoreKey, string> = {
  ios: KELIRIJI_IOS_STORE_URL,
  huawei: ANDROID_HTTPS.huawei,
  honor: ANDROID_HTTPS.honor,
  xiaomi: ANDROID_HTTPS.xiaomi,
  oppo: ANDROID_HTTPS.oppo,
  oneplus: ANDROID_HTTPS.oppo,
  vivo: ANDROID_HTTPS.vivo,
  tencent: ANDROID_HTTPS.tencent,
};

export function storeHref(key: StoreKey): string {
  return STORE_HREFS[key];
}

export type ClientKind = {
  os: 'ios' | 'android' | 'other';
  channel: StoreKey | 'fallback';
  inAppBlocker: boolean;
};

export function detectClient(ua: string, opts?: { platform?: string; maxTouchPoints?: number }): ClientKind {
  const u = ua.toLowerCase();
  const inAppBlocker = /micromessenger|wxwork/.test(u);
  const iPadOs = opts?.platform === 'MacIntel' && (opts.maxTouchPoints || 0) > 1;
  const isIOS = /iphone|ipad|ipod/.test(u) || iPadOs;
  const isAndroid = /android/.test(u);

  let channel: ClientKind['channel'] = 'fallback';
  if (/honor/.test(u)) channel = 'honor';
  else if (/huawei|harmonyos|hmos/.test(u)) channel = 'huawei';
  else if (/miui|xiaomi|redmi|\bpoco\b/.test(u)) channel = 'xiaomi';
  else if (/oneplus|oxygen/.test(u)) channel = 'oneplus';
  else if (/oppo|heytap|realme|oplus|coloros/.test(u)) channel = 'oppo';
  else if (/vivo|iqoo/.test(u)) channel = 'vivo';

  return {
    os: isIOS ? 'ios' : isAndroid ? 'android' : 'other',
    channel,
    inAppBlocker,
  };
}

export function matchedTileId(
  client: ClientKind,
  forcedStore: StoreKey | null,
): StoreTileId | null {
  const key = forcedStore || (client.os === 'ios' ? 'ios' : client.os === 'android' ? client.channel : null);
  if (!key) return null;
  if (key === 'fallback') return 'tencent';
  return key;
}

export function resolveJumpUrl(client: ClientKind, forcedStore: StoreKey | null): string | null {
  if (forcedStore) return storeHref(forcedStore);
  if (client.inAppBlocker || client.os === 'other') return null;
  if (client.os === 'ios') return KELIRIJI_IOS_STORE_URL;
  if (client.os === 'android') {
    if (client.channel === 'fallback') return ANDROID_HTTPS.tencent;
    return storeHref(client.channel);
  }
  return null;
}

export function downloadUrlWithStore(store?: StoreKey | null): string {
  if (!store) return KELIRIJI_DOWNLOAD_URL;
  return `${KELIRIJI_DOWNLOAD_URL}?store=${store}`;
}
